import React, { useState, useEffect, useMemo } from 'react';
import { IItemModifierGroups, IItemModifierGroupsPaginatedInfo, IGetModifierGroupPaginatedInfo } from 'types';
import { useFetchData } from 'hooks/useFetchData';
import { getModifierGroupPaginatedInfo } from 'backend/modifierGroups';
import { useTranslation } from 'react-i18next';
import RestaurantsScrollableList from 'components/Lists/ListView/RestaurantsScrollableList';
import Text from 'components/Text/Text';
import ModifiersScrollView from './components/ModifiersScrollView';
import DishesScrollView from './components/DishesScrollView';
import classNames from 'classnames';
import style from './Info.module.scss';

interface IInfo {
	isOpenInfo: boolean;
	data: IItemModifierGroups;
}

const Info: React.FC<IInfo> = ({ isOpenInfo, data }) => {
	const [modifierGroup, setModifierGroup] = useState<IItemModifierGroupsPaginatedInfo>();
	const { response, loading, refetch } = useFetchData<IGetModifierGroupPaginatedInfo, string>(
		isOpenInfo && !modifierGroup ? getModifierGroupPaginatedInfo : null,
		data.id,
	);
	const containerClasses = classNames(style.container, { [style.visible]: isOpenInfo });
	const { t } = useTranslation('listing');

	useEffect(() => {
		if (isOpenInfo) {
			refetch();
		}
	}, [isOpenInfo]);

	useEffect(() => {
		if (response) {
			setModifierGroup(response.data);
		}
	}, [response]);

	const restaurants = useMemo(
		() =>
			data?.restaurantsModifierGroups.map((el) => ({
				...el,
				id: el.restaurantId,
			})),
		[data],
	);

	return (
		<div className={containerClasses} data-test="Info">
			<div className={style.wrapper}>
				<div className={style.block}>
					<Text
						className={style.blockName}
						translationNameSpace="listing"
						translationKey="nameOfModifierGroupLabel"
						theme="neutralLight"
					/>
					<Text className={style.blockValue} translationKey={data.name} withTooltip={true} />
					<Text
						className={style.blockName}
						translationNameSpace="listing"
						translationKey="customerLabel"
						theme="neutralLight"
					/>
					<Text className={style.blockValue} translationKey={data.customerName} withTooltip={true} />
					<Text
						className={style.blockName}
						translationNameSpace="listing"
						translationKey="externalIdLabel"
						theme="neutralLight"
					/>
					<Text
						className={style.blockValue}
						translationKey={loading ? t('loadingLabel') : modifierGroup?.externalId}
						withTooltip={true}
					/>
				</div>
				<div className={style.block}>
					<Text
						className={style.blockName}
						translationNameSpace="listing"
						translationKey="restaurantLabel"
						theme="neutralLight"
					/>
					<div className={style.blockValue}>
						<RestaurantsScrollableList restaurants={restaurants} />
					</div>
				</div>
				<div className={style.block}>
					<Text
						className={style.blockName}
						translationNameSpace="listing"
						translationKey="modifiersLabel"
						theme="neutralLight"
					/>
					<div className={style.blockValue}>
						{loading ? t('loadingLabel') : <ModifiersScrollView data={modifierGroup?.modifiers || []} />}
					</div>
				</div>
				<div className={style.block}>
					<Text
						className={style.blockName}
						translationNameSpace="listing"
						translationKey="dishesLabel"
						theme="neutralLight"
					/>
					<div className={style.blockValue}>
						{loading ? t('loadingLabel') : <DishesScrollView data={modifierGroup?.dishes || []} />}
					</div>
				</div>
			</div>
		</div>
	);
};

export default React.memo(Info);
